import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agencyInfo } from '../../../mocks/dashboard';
import type { ServiceType, PayMethod } from '../../../mocks/bookings';

const brand = agencyInfo.brandColor;

const serviceTypes: ServiceType[] = ['Package', 'Flight', 'Hotel', 'Tour', 'Visa', 'Transfer', 'Cruise'];
const serviceTypeIcons: Record<ServiceType, string> = {
  Package:  'ri-suitcase-line',
  Flight:   'ri-flight-takeoff-line',
  Hotel:    'ri-hotel-line',
  Tour:     'ri-map-2-line',
  Visa:     'ri-passport-line',
  Transfer: 'ri-car-line',
  Cruise:   'ri-ship-line',
};

const suggestedCustomers = [
  { name: 'Ahmed Al-Rashid', phone: '+971 50 123 4567', email: 'ahmed.rashid@email.com', avatar: 'AA' },
  { name: 'Emma Williams', phone: '+971 52 890 1234', email: 'emma.w@email.co.uk', avatar: 'EW' },
  { name: 'David Chen', phone: '+971 52 345 6780', email: 'd.chen@enterprise.sg', avatar: 'DC' },
  { name: 'Omar Hassan', phone: '+971 56 901 2345', email: 'omar.hassan@hotmail.com', avatar: 'OH' },
];

const agents = ['Mohammed Khalid', 'Sara Ahmed', 'Fatima Al-Zahra'];
const payMethods: PayMethod[] = ['Bank Transfer', 'Credit Card', 'Cash', 'Cheque'];

interface FormData {
  // Step 1 – Customer
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerAvatar: string;
  // Step 2 – Trip
  serviceType: ServiceType;
  serviceName: string;
  destination: string;
  travelDate: string;
  returnDate: string;
  pax: number;
  agent: string;
  notes: string;
  // Step 3 – Supplier
  supplierName: string;
  supplierType: string;
  supplierContact: string;
  supplierPhone: string;
  supplierEmail: string;
  supplierRef: string;
  costPrice: string;
  sellPrice: string;
  // Step 4 – Payment
  collectDeposit: boolean;
  depositAmount: string;
  payMethod: PayMethod;
  payRef: string;
}

const INIT: FormData = {
  customerName: '', customerPhone: '', customerEmail: '', customerAvatar: '',
  serviceType: 'Package', serviceName: '', destination: '', travelDate: '', returnDate: '', pax: 2, agent: 'Mohammed Khalid', notes: '',
  supplierName: '', supplierType: '', supplierContact: '', supplierPhone: '', supplierEmail: '', supplierRef: '', costPrice: '', sellPrice: '',
  collectDeposit: false, depositAmount: '', payMethod: 'Bank Transfer', payRef: '',
};

const steps = [
  { label: 'Customer', icon: 'ri-user-line' },
  { label: 'Trip Details', icon: 'ri-map-pin-line' },
  { label: 'Supplier', icon: 'ri-store-2-line' },
  { label: 'Payment', icon: 'ri-money-dollar-circle-line' },
];

export default function NewBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(INIT);
  const [submitted, setSubmitted] = useState(false);

  const set = (field: keyof FormData, value: string | number | boolean) =>
    setForm((f) => ({ ...f, [field]: value }));

  const selectCustomer = (c: typeof suggestedCustomers[0]) => {
    setForm((f) => ({
      ...f,
      customerName: c.name,
      customerPhone: c.phone,
      customerEmail: c.email,
      customerAvatar: c.avatar,
    }));
  };

  const margin = form.sellPrice && form.costPrice
    ? Number(form.sellPrice) - Number(form.costPrice)
    : 0;
  const marginPct = form.sellPrice && Number(form.sellPrice) > 0
    ? Math.round((margin / Number(form.sellPrice)) * 100)
    : 0;

  const handleSubmit = () => setSubmitted(true);

  if (submitted) {
    const newId = `BK-${1085 + Math.floor(Math.random() * 10)}`;
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
          style={{ backgroundColor: `${brand}15` }}
        >
          <i className="ri-checkbox-circle-fill text-4xl" style={{ color: brand }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Created!</h2>
        <p className="text-gray-500 mb-1">
          <span className="font-bold" style={{ color: brand }}>{newId}</span> has been successfully created.
        </p>
        <p className="text-sm text-gray-400 mb-8">{form.customerName} · {form.serviceName} · {form.destination}</p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/bookings')}
            className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl text-white cursor-pointer"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-list-check-2" />
            View All Bookings
          </button>
          <button
            onClick={() => { setForm(INIT); setStep(0); setSubmitted(false); }}
            className="flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
          >
            <i className="ri-add-line" />
            Create Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/bookings')}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Create New Booking</h1>
          <p className="text-sm text-gray-500 mt-0.5">Fill in all steps to create a complete booking record</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center mb-8">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all"
                style={
                  i < step
                    ? { backgroundColor: brand, color: 'white' }
                    : i === step
                    ? { backgroundColor: brand, color: 'white', boxShadow: `0 0 0 4px ${brand}25` }
                    : { backgroundColor: '#F3F4F6', color: '#9CA3AF' }
                }
              >
                {i < step ? <i className="ri-check-line" /> : <i className={`${s.icon}`} />}
              </div>
              <span
                className="text-xs font-semibold whitespace-nowrap"
                style={{ color: i <= step ? brand : '#9CA3AF' }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="flex-1 h-0.5 mx-3 mb-5 rounded-full"
                style={{ backgroundColor: i < step ? brand : '#E5E7EB' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Cards */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">

        {/* STEP 1 — Customer */}
        {step === 0 && (
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Customer Information</h3>
            <p className="text-sm text-gray-400 mb-5">Select an existing customer or enter new details</p>

            {/* Quick Select */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {suggestedCustomers.map((c) => (
                <button
                  key={c.name}
                  onClick={() => selectCustomer(c)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all text-left"
                  style={
                    form.customerName === c.name
                      ? { borderColor: brand, backgroundColor: `${brand}08` }
                      : { borderColor: '#E5E7EB', backgroundColor: 'white' }
                  }
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: brand }}
                  >
                    {c.avatar}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.phone}</p>
                  </div>
                  {form.customerName === c.name && (
                    <i className="ri-checkbox-circle-fill ml-auto flex-shrink-0 text-lg" style={{ color: brand }} />
                  )}
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Or enter manually</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Full Name', field: 'customerName' as keyof FormData, placeholder: 'e.g. John Smith' },
                { label: 'Phone', field: 'customerPhone' as keyof FormData, placeholder: '+971 50 000 0000' },
                { label: 'Email', field: 'customerEmail' as keyof FormData, placeholder: 'john@email.com' },
              ].map((f) => (
                <div key={f.field} className={f.field === 'customerEmail' ? 'col-span-2' : ''}>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">{f.label}</label>
                  <input
                    type="text"
                    value={form[f.field] as string}
                    onChange={(e) => set(f.field, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — Trip Details */}
        {step === 1 && (
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Trip Details</h3>
            <p className="text-sm text-gray-400 mb-5">Set the service, destination, dates and pax</p>

            {/* Service Type */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Service Type</label>
              <div className="flex flex-wrap gap-2">
                {serviceTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => set('serviceType', t)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium cursor-pointer transition-all"
                    style={
                      form.serviceType === t
                        ? { borderColor: brand, backgroundColor: `${brand}10`, color: brand }
                        : { borderColor: '#E5E7EB', color: '#6B7280' }
                    }
                  >
                    <i className={`${serviceTypeIcons[t]} text-sm`} />
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Service Name / Description</label>
                <input
                  type="text"
                  value={form.serviceName}
                  onChange={(e) => set('serviceName', e.target.value)}
                  placeholder="e.g. Honeymoon Package — Maldives 7N"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Destination</label>
                <input
                  type="text"
                  value={form.destination}
                  onChange={(e) => set('destination', e.target.value)}
                  placeholder="e.g. Maldives"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Passengers (Pax)</label>
                <input
                  type="number"
                  min="1"
                  value={form.pax}
                  onChange={(e) => set('pax', Number(e.target.value))}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Travel Date</label>
                <input
                  type="date"
                  value={form.travelDate}
                  onChange={(e) => set('travelDate', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Return Date</label>
                <input
                  type="date"
                  value={form.returnDate}
                  onChange={(e) => set('returnDate', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Assigned Agent</label>
                <select
                  value={form.agent}
                  onChange={(e) => set('agent', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50 focus:outline-none cursor-pointer"
                >
                  {agents.map((a) => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Notes / Special Requests</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Special requests, dietary needs, preferences..."
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 resize-none focus:outline-none bg-gray-50 placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Supplier */}
        {step === 2 && (
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Supplier & Pricing</h3>
            <p className="text-sm text-gray-400 mb-5">Enter supplier details and set cost vs sell price</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Supplier Name', field: 'supplierName' as keyof FormData, placeholder: 'e.g. Soneva Fushi Resort', span: 2 },
                { label: 'Supplier Type', field: 'supplierType' as keyof FormData, placeholder: 'e.g. Hotel / Resort, DMC, Tour Operator' },
                { label: 'Contact Person', field: 'supplierContact' as keyof FormData, placeholder: 'e.g. Mia Hassan' },
                { label: 'Supplier Phone', field: 'supplierPhone' as keyof FormData, placeholder: '+960 000 0000' },
                { label: 'Supplier Email', field: 'supplierEmail' as keyof FormData, placeholder: 'info@supplier.com' },
                { label: 'Booking Reference', field: 'supplierRef' as keyof FormData, placeholder: 'e.g. SF-2026-8842', span: 2 },
              ].map((f) => (
                <div key={f.field} className={f.span === 2 ? 'col-span-2' : ''}>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">{f.label}</label>
                  <input
                    type="text"
                    value={form[f.field] as string}
                    onChange={(e) => set(f.field, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                  />
                </div>
              ))}

              {/* Pricing */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Cost Price (AED)</label>
                <input
                  type="number"
                  value={form.costPrice}
                  onChange={(e) => set('costPrice', e.target.value)}
                  placeholder="e.g. 10800"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Sell Price (AED)</label>
                <input
                  type="number"
                  value={form.sellPrice}
                  onChange={(e) => set('sellPrice', e.target.value)}
                  placeholder="e.g. 15000"
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Margin Preview */}
            {form.costPrice && form.sellPrice && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { label: 'Cost Price', value: `AED ${Number(form.costPrice).toLocaleString()}`, color: '#DC2626' },
                  { label: 'Sell Price', value: `AED ${Number(form.sellPrice).toLocaleString()}`, color: brand },
                  { label: `Profit (${marginPct}%)`, value: `AED ${margin.toLocaleString()}`, color: '#059669' },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl px-4 py-3" style={{ backgroundColor: `${s.color}08` }}>
                    <p className="text-xs mb-0.5" style={{ color: s.color }}>{s.label}</p>
                    <p className="text-base font-bold" style={{ color: s.color }}>{s.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 4 — Payment */}
        {step === 3 && (
          <div>
            <h3 className="text-base font-bold text-gray-800 mb-1">Payment</h3>
            <p className="text-sm text-gray-400 mb-5">Record initial deposit or full payment (optional)</p>

            {/* Summary */}
            <div className="bg-gray-50 rounded-2xl p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer</span>
                <span className="font-semibold text-gray-800">{form.customerName || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Service</span>
                <span className="font-semibold text-gray-800">{form.serviceName || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sell Price</span>
                <span className="font-bold" style={{ color: brand }}>AED {form.sellPrice ? Number(form.sellPrice).toLocaleString() : '—'}</span>
              </div>
            </div>

            {/* Collect Deposit Toggle */}
            <div
              onClick={() => set('collectDeposit', !form.collectDeposit)}
              className="flex items-center justify-between p-4 rounded-xl border cursor-pointer mb-4 transition-all"
              style={form.collectDeposit ? { borderColor: brand, backgroundColor: `${brand}08` } : { borderColor: '#E5E7EB' }}
            >
              <div className="flex items-center gap-3">
                <i className="ri-money-dollar-circle-line text-xl" style={{ color: form.collectDeposit ? brand : '#9CA3AF' }} />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Record a payment now</p>
                  <p className="text-xs text-gray-400">Record deposit or full payment against this booking</p>
                </div>
              </div>
              <div
                className="w-11 h-6 rounded-full transition-all flex items-center px-0.5 flex-shrink-0"
                style={{ backgroundColor: form.collectDeposit ? brand : '#D1D5DB' }}
              >
                <div
                  className="w-5 h-5 bg-white rounded-full shadow transition-all"
                  style={{ transform: form.collectDeposit ? 'translateX(20px)' : 'translateX(0)' }}
                />
              </div>
            </div>

            {form.collectDeposit && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Amount (AED)</label>
                  <input
                    type="number"
                    value={form.depositAmount}
                    onChange={(e) => set('depositAmount', e.target.value)}
                    placeholder="e.g. 7500"
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Payment Method</label>
                  <select
                    value={form.payMethod}
                    onChange={(e) => set('payMethod', e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50 focus:outline-none cursor-pointer"
                  >
                    {payMethods.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Payment Reference</label>
                  <input
                    type="text"
                    value={form.payRef}
                    onChange={(e) => set('payRef', e.target.value)}
                    placeholder="e.g. TRF-27032026-001"
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none bg-gray-50 placeholder-gray-400"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-100">
          <button
            onClick={() => step === 0 ? navigate('/bookings') : setStep((s) => s - 1)}
            className="flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full transition-all"
                style={{ backgroundColor: i === step ? brand : i < step ? `${brand}60` : '#E5E7EB' }}
              />
            ))}
          </div>

          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: brand }}
            >
              Next Step
              <i className="ri-arrow-right-line" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: '#059669' }}
            >
              <i className="ri-checkbox-circle-line" />
              Create Booking
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
